import { formatWeight, parseWeight, weightNumber } from '@tahi/core';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { clearWeightGoal, setWeightGoal } from '../../../data/commands/weight';
import { useCommandEnv } from '../../../data/hooks';
import { Button, ScreenHeader, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { GoalBar } from '../components/GoalBar';
import { copy, errorText } from '../copy';
import { useWeightOverview, WEIGHT_BASE } from '../hooks';
import { CARD, SCREEN } from '../styles';

/** Set, change or remove the optional goal weight. Progress counts from the weight right now. */
export function GoalScreen() {
  const o = useWeightOverview();
  const env = useCommandEnv();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const goalG = o?.settings.goalG ?? null;
  const unit = o?.unit ?? 'kg';
  useEffect(() => {
    if (goalG !== null) setText(weightNumber(goalG, unit));
  }, [goalG, unit]);

  if (!o) {
    return (
      <main className={SCREEN}>
        <p role="status" className="text-muted">
          {copy.loading}
        </p>
      </main>
    );
  }
  const current = o.avg7 ?? o.latest?.valueG ?? null;

  return (
    <main className={SCREEN}>
      <ScreenHeader
        title={o.settings.goalG !== null ? copy.editGoal : copy.setGoal}
        back={{ to: WEIGHT_BASE, label: copy.moduleName }}
      />
      <p className="text-muted">{copy.goalOptional}</p>

      {o.goal ? (
        <section aria-label={copy.goal} className={CARD}>
          <GoalBar goal={o.goal} unit={unit} />
        </section>
      ) : null}

      {current === null ? (
        <p className="text-fg">{copy.goalNeedsWeight}</p>
      ) : (
        <form
          className={`${CARD} flex flex-col gap-4`}
          onSubmit={async (e) => {
            e.preventDefault();
            const parsed = parseWeight(text, unit);
            if (!parsed.ok) return setError(errorText(parsed.error));
            const r = await setWeightGoal(env, { goalG: parsed.grams, startG: current });
            if (!r.ok) return setError(errorText(r.error));
            showToast({ message: copy.goalSaved });
            navigate(WEIGHT_BASE);
          }}
        >
          <p className="text-sm text-muted">
            {copy.average7}: {formatWeight(current, unit)}
          </p>
          <TextField
            label={`${copy.goalWeight} (${unit})`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={text}
            error={error}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" type="submit">
              {copy.saveGoal}
            </Button>
            {o.settings.goalG !== null ? (
              <Button
                variant="quiet-destructive"
                onClick={async () => {
                  await clearWeightGoal(env);
                  setText('');
                  showToast({ message: copy.goalRemoved });
                  navigate(WEIGHT_BASE);
                }}
              >
                {copy.removeGoal}
              </Button>
            ) : null}
          </div>
        </form>
      )}
    </main>
  );
}
